import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as R from 'ramda'
import { type Nil, Utils } from '@pbt/pbt-ui-components'

import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { InventoryCategoryName } from '~/constants/inventoryCategory'
import InvoiceType from '~/constants/InvoiceType'
import { OrderType } from '~/constants/SOAPStates'
import { cancelRefills } from '~/store/actions/orders'
import { useGetChargeSheetId } from '~/store/hooks/chargeSheet'
import { useOpenInvoice } from '~/store/hooks/finance'
import {
  useLogItemStateGetter,
  useNavigateOrderToPrescription,
  useOpenPrescriptionDialogAfterFetchUnifiedOrder,
  useRefillDialog,
} from '~/store/hooks/orders'
import { useCreateOrUpdatePrescription } from '~/store/hooks/prescription'
import { getCurrentBusiness } from '~/store/reducers/auth'
import {
  getFeatureToggle,
  getInventoryCategory,
} from '~/store/reducers/constants'
import { getOrdersIsSending } from '~/store/reducers/orders'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type {
  CRUDOrderOptions,
  Order,
  Prescription,
  PrescriptionSnapshotItem,
} from '~/types'
import { addOriginalBusinessId } from '~/utils'
import { getInventoryItemCategoryId } from '~/utils/orderUtils'
import {
  getIdFromTimelineItem,
  timelinePrescriptionToRegularPrescription,
} from '~/utils/prescriptionUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

const usePrescriptions = ({
  clientId,
  patientId,
}: {
  clientId: string
  patientId: string
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const navigateToPrescriptionDialog = useNavigateOrderToPrescription()
  const createOrUpdatePrescription = useCreateOrUpdatePrescription()
  const logItemStateGetter = useLogItemStateGetter()
  const chargeSheetId = useGetChargeSheetId(clientId)

  const [openRefillDialog, closeRefillDialog] = useRefillDialog()
  const closeRefillDialogAfterSave = useCloseAfterCreation(
    closeRefillDialog,
    getOrdersIsSending,
  )
  const [openInvoiceDialog] = useDialog(DialogNames.INVOICE)
  const [openPrintInHouseOrderLabelDialog] = useDialog(
    DialogNames.PRINT_IN_HOUSE_ORDER_LABEL,
  )

  const openInvoice = useOpenInvoice(clientId, openInvoiceDialog)

  const business = useSelector(getCurrentBusiness)
  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))
  const InventoryCategory = useSelector(getInventoryCategory)

  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const DrugId = Utils.findConstantIdByName(
    InventoryCategoryName.DRUG,
    InventoryCategory,
  )
  const VetDietId = Utils.findConstantIdByName(
    InventoryCategoryName.VET_DIET,
    InventoryCategory,
  )
  const FoodId = Utils.findConstantIdByName(
    InventoryCategoryName.FOOD,
    InventoryCategory,
  )
  const openPrescriptionDialog = (
    order: Prescription,
    originalBusinessId: string | Nil,
  ) => {
    const categoryId = getInventoryItemCategoryId(order, isFoodCatalogEnabled)

    if (categoryId === DrugId) {
      navigateToPrescriptionDialog(
        InventoryCategoryName.DRUG,
        order,
        originalBusinessId,
      )
    } else if (categoryId === VetDietId) {
      navigateToPrescriptionDialog(
        InventoryCategoryName.VET_DIET,
        order,
        originalBusinessId,
      )
    } else if (categoryId === FoodId) {
      navigateToPrescriptionDialog(
        InventoryCategoryName.FOOD,
        order,
        originalBusinessId,
      )
    }
  }

  const handleGoToSoap = (
    soapId: string,
    originalBusinessId: string | Nil,
    prescription?: Prescription,
  ) => {
    if (!prescription) {
      navigate(
        addOriginalBusinessId(
          `/soap/${soapId}`,
          isPatientSharingEnabled ? originalBusinessId : null,
        ),
      )
    } else {
      openPrescriptionDialog(
        { ...prescription, type: OrderType.PRESCRIPTION, soapId },
        originalBusinessId,
      )
    }
  }

  const handleGoToInvoice = (item: PrescriptionSnapshotItem | Prescription) => {
    const prescriptionSnapshotItem = 'prescription' in item

    openInvoice({
      clientId,
      patientId,
      prescriptionId: prescriptionSnapshotItem
        ? item.prescription?.id
        : item.id,
      soapId: item.soapId,
      logId: prescriptionSnapshotItem
        ? R.path(['prescription', 'id'], item)
        : item.id,
      logType: OrderType.PRESCRIPTION,
      invoiceId: getIdFromTimelineItem(item),
    })
  }

  const { fetchUnifiedOrderAndOpenPrescription } =
    useOpenPrescriptionDialogAfterFetchUnifiedOrder({
      overrideParams: {
        outsideSoap: true,
        skipOrderFetching: false,
        soapId: undefined,
      },
    })

  const handleNavigateToItemEditableDetails = ({
    soapId,
    prescription,
    businessId,
  }: {
    businessId: string
    prescription: Prescription
    soapId?: string
  }) => {
    if (soapId) {
      handleGoToSoap(soapId, businessId, prescription)
    } else {
      fetchUnifiedOrderAndOpenPrescription(prescription.id)
    }
  }

  const handlePrint = (item: PrescriptionSnapshotItem | Prescription) => {
    const prescriptionSnapshotItem = 'prescription' in item

    openPrintInHouseOrderLabelDialog({
      printInfo: {
        ...timelinePrescriptionToRegularPrescription(
          prescriptionSnapshotItem ? item.prescription : item,
          {
            name: item.name,
            masterName: item.masterName || '',
            prescription: prescriptionSnapshotItem ? item.prescription : item,
          },
          isFoodCatalogEnabled,
        ),
        client,
        patient,
        business,
      },
    })
  }

  const createChargeSheetRefill = (
    refill: Prescription,
    options: CRUDOrderOptions,
  ) => {
    createOrUpdatePrescription(
      logItemStateGetter({ ...refill, items: [] } as Order)!,
      {
        ...options,
        outsideSoap: true,
        invoiceId: chargeSheetId,
        soapId: refill.soapId || undefined,
      },
    )
    if (options.print) {
      closeRefillDialogAfterSave()
    }
  }

  const handleDispenseRefill = (
    item: PrescriptionSnapshotItem | Prescription,
  ) => {
    const prescriptionSnapshotItem = 'prescription' in item

    const refillItem = timelinePrescriptionToRegularPrescription(
      prescriptionSnapshotItem ? item.prescription : item,
      {
        name: item.name,
        masterName: item.masterName || '',
        prescription: prescriptionSnapshotItem ? item.prescription : item,
      },
      isFoodCatalogEnabled,
    )
    openRefillDialog({
      outsideSoap: true,
      isInvoice: false,
      prescription: {
        ...R.omit(['id', 'soapId'], refillItem),
        parent: { ...R.omit(['soapId'], refillItem) },
      },
      onSave: (refill: Prescription, options: CRUDOrderOptions) =>
        createChargeSheetRefill(refill, options),
    })
  }

  const handleCancelRefills = (prescription: Prescription) =>
    dispatch(cancelRefills(prescription.id))

  const handleAddPrescription = () =>
    openInvoice({
      clientId,
      patientId,
      chargesEntityType: InvoiceType.CHARGE_SHEET,
      create: true,
      isOtcInvoice: true,
    })

  return {
    handleGoToSoap,
    handleGoToInvoice,
    handleNavigateToItemEditableDetails,
    handlePrint,
    handleDispenseRefill,
    handleCancelRefills,
    handleAddPrescription,
  }
}

export default usePrescriptions
